import { Record, List, Map, fromJS } from 'immutable';
import _ from 'lodash';
import { DIRECTCountries, PPGFCountries } from '@joindeed/paypal-public';
import { DonationProvider } from '@joindeed/calculate-fees';

import {
  DonationDisbursementType,
  NonprofitIdentifierEntry,
  OpportunityProvider,
  VolunteerReward,
} from 'generated/graphql';
import countries from 'utils/countries';

import LocationLatLng from '../locationLatLng/model';
import LocationObject from '../locationObject/model';
import Location from '../location/model';

interface Stripe {
  userId: string;
  customerId: string;
}

interface Feature {
  enabled: boolean;
  locations: string[];
  locationsExclude: boolean;
}

interface Features {
  [propName: string]: Feature;
}

interface Taxonomy {
  irsFoundationCode?: string;
  irsSubSectionCode?: string;
  irsClassificationCode?: string;
  externalClassification?: string;
  ncesSchoolType?: string;
  ncesSchoolDistrict?: string;
}

interface NonprofitContact {
  firstName: string;
  lastName: string;
  phone: string;
  salutation: string;
  address: string;
}

export interface NotificationBanner {
  isActive: boolean;
  bannerText: string;
  linkIsActive: boolean;
  linkText: string;
  linkUrl: string;
  backgroundColor: string;
  textColor: string;
  locations: string[];
  locationsExclude: boolean;
}

export enum NonprofitEntityType {
  nonprofit = 'nonprofit',
  school = 'school',
  governmentEntity = 'governmentEntity',
  memberAssociatedEntity = 'memberAssociatedEntity',
}

export interface Settings {
  deedFeeCapUSD?: number;
  feeCoveredBy?: 'User' | 'Deed' | 'Company';
  applicationFees?: {
    corporateDonationFeeCapUSD?: number;
    corporate?: number;
    employee?: number;
  };
  disabledOpportunityProviders?: OpportunityProvider[];
  actionDonationRewardRequired: boolean;
  searchNonprofitDB?: boolean;
  sigmaEditAbility?: boolean;
  volunteerReward?: VolunteerReward;
  allowExtraContactEmails?: boolean;
}

export interface EmailSignature {
  main?: string;
}

export interface PartnerCompany {
  name: string;
  status: string;
  partnershipLogo?: string;
  partnershipWebsiteUrl?: string;
}

export interface SSO2 {
  provider: string;
  identifier: string;
  organizerSsoEnabled?: boolean;
  adminSsoEnabled?: boolean;
  allowSiblingSubsidiaryLogin?: boolean;
}

export type ParentCompany = Pick<Organization, 'id' | 'emailDomains'> &
  Partial<Pick<Organization, 'sso2'>>;

export type DisclaimerContentType = 'IMAGE' | 'TEXT';

export interface Disclaimer {
  _id: string;
  contentType: DisclaimerContentType;
  content: string;
  buttonText?: string;
  disclaimerExpire: number; // Disclaimer expire in minutes
  locationsExclude: boolean;
  locations: string[];
  startingAt?: Date;
  endingAt?: Date;
}

const properties = {
  id: '',
  externalId: '',
  nonprofitId: '',
  name: '',
  legalName: '',
  email: '',
  phone: '',
  pictures: [],
  mainPicture: '',
  logo: '',
  faqLink: '',
  website: '',
  location: '',
  locations: Map(),
  departments: List(),
  locationObject: new LocationObject(),
  rating: Number(),
  brief: '',
  modifiedAt: Object(),
  createdAt: Object(),
  reviews: Object(),
  type: '',
  disclaimers: List(),
  pillars: List(),
  ERGs: List(),
  categories: List(),
  SDGs: List(),
  facebookPage: '',
  xSocial: '',
  instagram: '',
  brandColor: '',
  homepageBanner: '',
  homepageTagline: '',
  hideHomepageTagline: false,
  homepageGradient: true,
  ein: '',
  stripe: Object(),
  features: undefined,
  settings: undefined,
  emailSignature: undefined,
  emailSenderName: undefined,
  acceptsDonations: false,
  currencyCode: '',
  paypalPayerId: '',
  payPalModel: undefined,
  paypalCharityType: undefined,
  shareLink: '',
  excludeCauses: [],
  notificationBanner: {},
  affiliationsCount: undefined,
  appVanityDomain: undefined,
  splashBackgroundImage: '',
  payrollFlatFile: undefined,
  parent: undefined,
  siblingSubsidiaries: [],
  donationProvidersAvailability: {},
  disbursementMethods: {},
  governmentInformationLetterUploaded: false,
  governmentInformationLetter: undefined,
  letterOfAssociation: undefined,
  nonprofitEntityType: undefined,
  taxonomy: undefined,
  isParentClaimed: false,
  identifiers: [],
  partnerCompanies: [],
  geographicalArea: '',
  boardMembers: [],
  orgIncorporationState: '',
  contact: undefined,
  nonprofitDataValidatedAt: undefined,
  verificationStatus: undefined,
  donationProviders: {},
  donateViaNonprofitId: undefined,
  emailDomains: [],
  code: '',
  sso2: undefined,
  disbursementConfiguration: {},
};

export default class Organization extends Record(properties, 'Organization') {
  public readonly id!: string;

  public readonly externalId!: string;

  public readonly nonprofitId!: string;

  public readonly name!: string;

  public readonly legalName!: string;

  public readonly email!: string;

  public readonly phone!: string;

  public readonly pictures!: string[];

  public readonly emailDomains!: string[];

  public readonly code!: string;

  public readonly sso2?: SSO2;

  public readonly mainPicture!: string;

  public readonly logo!: string;

  public readonly faqLink!: string;

  public readonly website!: string;

  public readonly location!: string;

  public readonly locations!: Map<string, Location>;

  public readonly departments!: List<string>;

  public readonly locationObject!: LocationObject;

  public readonly rating!: number;

  public readonly brief!: string;

  public readonly modifiedAt!: Date;

  public readonly createdAt!: Date;

  public readonly reviews!: any;

  public readonly type!: string;

  public readonly disclaimers!: List<Disclaimer>[];

  public readonly pillars!: List<string>;

  public readonly ERGs!: List<string>;

  public readonly categories!: List<string>;

  public readonly SDGs!: List<string>;

  public readonly facebookPage!: string;

  public readonly instagram!: string;

  public readonly xSocial!: string;

  public readonly brandColor!: string;

  public readonly homepageBanner!: string;

  public readonly homepageGradient!: boolean;

  public readonly homepageTagline!: string;

  public readonly hideHomepageTagline!: boolean;

  public readonly ein!: string;

  public readonly stripe!: Stripe;

  public readonly donationMatchingRules!: List<any>;

  public readonly features!: Features;

  public readonly acceptsDonations!: boolean;

  public readonly currencyCode!: string;

  public readonly paypalPayerId!: string;

  public readonly payPalModel?: 'PPGF' | 'DIRECT' | 'COMMERCE';

  public readonly paypalCharityType!: string;

  public readonly shareLink!: string;

  public readonly excludeCauses!: string[];

  public readonly notificationBanner!: NotificationBanner;

  public readonly settings?: Settings;

  public readonly emailSignature?: EmailSignature;

  public readonly emailSenderName?: string;

  public readonly affiliationsCount!: number;

  public readonly appVanityDomain!: string;

  public readonly splashBackgroundImage!: string;

  public readonly payrollFlatFile!: Map<string, string>;

  public readonly parent?: string | ParentCompany;

  public readonly siblingSubsidiaries?: Array<Partial<Organization> & Pick<Organization, 'id'>>;

  public readonly donationProvider?: DonationProvider;

  public readonly donationProvidersAvailability?: Record<DonationProvider, boolean>;

  public readonly disbursementMethods?: Record<DonationProvider, string>;

  public readonly disbursementConfiguration?: {
    invoicing: Record<DonationDisbursementType, boolean>;
    invoiceSettings: {
      paymentDueDate: {
        offsetType: 'OFFSET' | 'FIXED';
        valueInDays: number;
      };
    };
  };

  public readonly governmentInformationLetterUploaded!: boolean;

  public readonly invisible?: boolean;

  public readonly deletedAt?: Date;

  public readonly governmentInformationLetter!: {
    fileUri: string;
    uploadedAt: Date;
  };

  public readonly letterOfAssociation!: {
    fileUri: string;
    uploadedAt: Date;
  };

  public readonly nonprofitEntityType!: NonprofitEntityType;

  public readonly verificationStatus!: 'active' | 'inactive' | 'pending';

  public readonly isParentClaimed!: boolean;

  public readonly taxonomy!: Taxonomy;

  public readonly identifiers!: NonprofitIdentifierEntry[];

  public readonly partnerCompanies?: Array<PartnerCompany>;

  public readonly geographicalArea?: string;

  public readonly boardMembers?: string[];

  public readonly orgIncorporationState?: string;

  public readonly contact?: NonprofitContact;

  public readonly nonprofitDataValidatedAt?: Date;

  public readonly donationProviders?: Record<DonationProvider, boolean>;

  public readonly donateViaNonprofitId?: string;

  constructor(values: any = {}) {
    const locationObject = new LocationObject(values.locationObject);
    const validValues = _.pick(values, Object.keys(properties));
    const { locations } = values;
    super({
      ...validValues,
      id: values.id || values._id,
      pillars: List(values.pillars),
      ERGs: List(values.ERGs),
      categories: List(values.categories),
      departments: List(values.departments),
      SDGs: List(values.SDGs),
      donationMatchingRules: fromJS(values.donationMatchingRules || []),
      type: values.type || values.__t, // eslint-disable-line no-underscore-dangle
      locationLatLng: new LocationLatLng(values.location),
      locationObject,
      location: String(locationObject),
      locations:
        locations && Map(locations.map((location: any) => [location.id, new Location(location)])),
      features: values.features ? fromJS(values.features) : undefined,
      settings: values.settings ? Map(values.settings) : undefined,
      emailSignature: values.emailSignature ? Map(values.emailSignature) : undefined,
      emailSenderName: values.emailSenderName || undefined,
      payrollFlatFile: values.payrollFlatFile ? Map(values.payrollFlatFile) : undefined,
      disclaimers: values.disclaimers
        ? List(values.disclaimers.map((disclaimer: Disclaimer) => Map(disclaimer)))
        : List(),
    });
  }

  isValid(): boolean {
    return !!(this.name && this.email && this.phone && this.brief && this.location);
  }

  isCompany(): boolean {
    return this.type.toLowerCase() === 'company';
  }

  isNonprofit(): boolean {
    return this.type.toLowerCase() === 'nonprofit';
  }

  isInStripeEligibleCountry(): boolean {
    return [
      // @NOTE-AE: Stripe doesn't support donations to nonprofits in these countries as of March 2024 - more details in https://support.stripe.com/questions/requirements-for-accepting-tips-or-donations
      // countries.HK.alpha2, # Hong Kong: Stripe cannot support donations
      // countries.IN.alpha2, # India: Stripe cannot support charities or donations to them
      countries.NZ.alpha2,
      countries.RO.alpha2,
      countries.SG.alpha2,
      countries.JP.alpha2,
      // countries.CH.alpha2, # Covered by PP now
    ].includes(this.locationObject?.countryCode);
  }

  isInPayPalEligibleCountry(): boolean {
    return (
      (PPGFCountries as readonly string[]).includes(this.locationObject?.countryCode) ||
      (DIRECTCountries as readonly string[]).includes(this.locationObject?.countryCode)
    );
  }

  isInPayPalDirectEligibleCountry(): boolean {
    return (DIRECTCountries as readonly string[]).includes(this.locationObject?.countryCode);
  }

  isInPPGFEligibleCountry(): boolean {
    return (PPGFCountries as readonly string[]).includes(this.locationObject?.countryCode);
  }

  isInNFGEligibleCountry(): boolean {
    return this.locationObject?.countryCode === 'US';
  }

  hasSetting(setting: any): boolean {
    return this.settings?.get(setting) === true;
  }
}
